import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';
import { theme } from '../../styles/theme';

interface CollectionRequest {
  request_id: string;
  country_code: string;
  country_name: string;
  metric_id: number;
  metric_name: string;
  collection_year: number;
  status: string;
  retry_count: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

const CollectionHistory: React.FC = () => {
  const [requests, setRequests] = useState<CollectionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUserRequests();
  }, [page, rowsPerPage]);

  const fetchUserRequests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/collection/requests/my', {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
      });
      // apiClient.get already unwraps the data field
      setRequests(response.requests || []);
      setTotalCount(response.total_count || 0);
    } catch (error) {
      console.error('Failed to fetch user requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: theme.colors.status.success }} fontSize="small" />;
      case 'failed':
        return <ErrorIcon sx={{ color: theme.colors.status.error }} fontSize="small" />;
      case 'pending':
        return <HourglassEmptyIcon sx={{ color: theme.colors.status.warning }} fontSize="small" />;
      case 'processing':
      case 'retrying':
        return <LoopIcon sx={{ color: theme.colors.status.info }} fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'retrying':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const filteredRequests = requests.filter(request =>
    request.country_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.metric_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.request_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress sx={{ color: theme.colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.text.primary,
            fontWeight: 400
          }}
        >
          My Collection Requests
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.colors.text.secondary,
            fontFamily: theme.fonts.primary
          }}
        >
          View the history of all your submitted collection requests
        </Typography>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by country, metric, or request ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            fontFamily: theme.fonts.primary,
            '& fieldset': {
              borderColor: theme.colors.border.light,
            },
            '&:hover fieldset': {
              borderColor: theme.colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.colors.primary.main,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.colors.text.secondary }} />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} sx={{
        borderRadius: theme.widget.borderRadius,
        boxShadow: theme.widget.boxShadow,
        '&:hover': {
          boxShadow: theme.widget.hoverBoxShadow,
        },
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Request ID</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Country</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Metric</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Year</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Status</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Retries</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Created</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => (
              <TableRow key={request.request_id}>
                <TableCell>
                  <Tooltip title={request.request_id}>
                    <Typography variant="caption" sx={{ fontFamily: theme.fonts.mono }}>
                      {request.request_id.substring(0, 12)}...
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                      {request.country_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.colors.text.secondary,
                        fontFamily: theme.fonts.primary
                      }}
                    >
                      {request.country_code}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={`ID: ${request.metric_id}`}>
                    <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                      {request.metric_name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary }}>{request.collection_year}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getStatusIcon(request.status)}
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </Box>
                  {request.error_message && (
                    <Tooltip title={request.error_message}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: theme.colors.status.error,
                          fontFamily: theme.fonts.primary
                        }}
                      >
                        Error: {request.error_message.substring(0, 30)}...
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {request.retry_count > 0 && (
                    <Chip
                      label={request.retry_count}
                      size="small"
                      variant="outlined"
                      color={request.retry_count > 3 ? 'error' : 'default'}
                      sx={{ fontFamily: theme.fonts.primary }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: theme.fonts.primary }}>
                    {formatDate(request.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: theme.fonts.primary }}>
                    {formatDate(request.updated_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filteredRequests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography
                    sx={{
                      py: 3,
                      color: theme.colors.text.secondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    {searchTerm
                      ? 'No requests match your search'
                      : 'No collection requests found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default CollectionHistory;